/**
 * MELON shop header (sections/melon-shop): category and audience taps update the page in place.
 *
 * Only what changes is touched - the pills and the product grid are replaced from the Section Rendering
 * API, the category rail just moves its ring - so nothing in the header redraws and the page never jumps.
 * Requests start on pointerdown and responses are cached per URL, so a tap and Back feel instant.
 * The header section is kept sticky so the pills stay under the site header while scrolling the grid.
 * Every control is a real link: any failure falls back to plain navigation.
 */
(function () {
  if (window.__melonShop) return;
  window.__melonShop = true;

  var cache = new Map();
  var pending = new Map();
  var busy = false;

  function q(sel, root) { return (root || document).querySelector(sel); }
  function sectionId(el) {
    var wrap = el && el.closest('.shopify-section');
    return wrap && wrap.id ? wrap.id.replace(/^shopify-section-/, '') : null;
  }
  function ids() {
    return { shop: sectionId(q('.cc-melon-shop')), grid: sectionId(q('.cc-collection-products')) };
  }
  function key(url) {
    var u = new URL(url, window.location.origin);
    u.searchParams.delete('page');
    return u.pathname + u.search;
  }

  // literal commas and no Accept header: with "Accept: application/json" Shopify answers null for every section
  function load(url) {
    var k = key(url);
    if (cache.has(k)) return Promise.resolve(cache.get(k));
    if (pending.has(k)) return pending.get(k);
    var s = ids();
    if (!s.shop || !s.grid) return Promise.reject(new Error('no sections'));
    var p = fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'sections=' + s.shop + ',' + s.grid, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data[s.shop] || !data[s.grid]) throw new Error('not a melon page');
        var parse = function (html) { return new DOMParser().parseFromString(html, 'text/html'); };
        var entry = { shop: parse(data[s.shop]), grid: parse(data[s.grid]) };
        cache.set(k, entry);
        pending.delete(k);
        return entry;
      })
      .catch(function (e) { pending.delete(k); throw e; });
    pending.set(k, p);
    return p;
  }

  function setLoading(on) {
    var shop = q('.cc-melon-shop');
    var grid = q('.cc-collection-products');
    if (shop) shop.classList.toggle('is-loading', on);
    if (grid) grid.classList.toggle('melon-grid-loading', on);
  }

  // replace, remove or insert one header block so it matches the fetched page; `after` is where a new one goes
  function sync(entry, sel, after) {
    var cur = q(sel);
    var fresh = q(sel, entry.shop);
    if (cur && fresh) cur.replaceWith(fresh.cloneNode(true));
    else if (cur && !fresh) cur.remove();
    else if (!cur && fresh) {
      var anchor = after && q(after);
      if (anchor) anchor.after(fresh.cloneNode(true));
      else q('.melon-shop').appendChild(fresh.cloneNode(true));
    }
  }

  function apply(entry, url, push, toGrid) {
    // 1. landing-only blocks (hero, audience tiles + perfume feature) and the pills - counts and links
    //    depend on the collection. The masthead itself stays; only its slim flag follows the page.
    sync(entry, '.melon-shop__hero', '.melon-shop__mast');
    sync(entry, '.melon-shop__intro', '.melon-shop__rail');
    sync(entry, '.melon-shop__pills', null);
    var mast = q('.melon-shop__mast');
    var freshMast = q('.melon-shop__mast', entry.shop);
    if (mast && freshMast) mast.className = freshMast.className;

    // 2. rail: move the ring only, so the thumbnails never reload
    var path = new URL(url, window.location.origin).pathname;
    var hit = null;
    document.querySelectorAll('.melon-shop__cat').forEach(function (a) {
      var on = new URL(a.href, window.location.origin).pathname === path;
      a.classList.toggle('is-on', on);
      if (on) { a.setAttribute('aria-current', 'page'); hit = a; } else a.removeAttribute('aria-current');
    });

    // 3. heading and product grid
    var h1 = q('.melon-shop h1');
    var freshH1 = q('h1', entry.shop);
    if (h1 && freshH1) h1.textContent = freshH1.textContent;
    var grid = q('.cc-collection-products');
    var freshGrid = q('.shopify-section', entry.grid);
    if (grid && freshGrid) grid.innerHTML = freshGrid.innerHTML;

    if (freshH1) document.title = freshH1.textContent.trim() + ' – ' + document.title.split(' – ').pop();
    if (push) window.history.pushState({ melonShop: true }, '', url);

    // keep the tapped category in view inside the rail, horizontally only
    if (hit) {
      var rail = hit.parentElement;
      var left = hit.offsetLeft - (rail.clientWidth - hit.clientWidth) / 2;
      rail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
    // bring the first products just under the pinned pills: always after a hero/tile/feature tap (their
    // result is below the fold), otherwise only if the products are above the viewport
    if (grid) {
      measure();
      var shop = q('.cc-melon-shop');
      var pillsNow = q('.melon-shop__pills');
      var offset = (pillsNow || shop) ? (pillsNow || shop).getBoundingClientRect().bottom : 0;
      var top = grid.getBoundingClientRect().top;
      if (toGrid) {
        var headerH = parseFloat(shop && shop.style.getPropertyValue('--melon-top')) || 0;
        var pillsH = pillsNow ? pillsNow.offsetHeight : 0;
        window.scrollTo({ top: window.pageYOffset + top - headerH - pillsH - 8, behavior: 'smooth' });
      } else if (top < offset) {
        window.scrollBy({ top: top - offset - 8, behavior: 'smooth' });
      }
    }
    document.dispatchEvent(new CustomEvent('melon-shop:updated', { detail: { url: url } }));
  }

  function go(url, push, toGrid) {
    if (busy) return;
    busy = true;
    var slow = window.setTimeout(function () { setLoading(true); }, 120);
    load(url)
      .then(function (entry) { apply(entry, url, push, toGrid); })
      .catch(function () { window.location.href = url; })
      .then(function () {
        window.clearTimeout(slow);
        setLoading(false);
        busy = false;
        measure();
      });
  }

  function link(e) {
    var a = e.target.closest && e.target.closest('a[data-melon-nav]');
    return a && a.origin === window.location.origin ? a : null;
  }

  // start the request as soon as a finger or pointer goes down
  document.addEventListener('pointerdown', function (e) {
    var a = link(e);
    if (a) load(a.href).catch(function () {});
  }, { passive: true });

  document.addEventListener('click', function (e) {
    var a = link(e);
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    go(a.href, true, a.hasAttribute('data-melon-scroll'));
  });

  window.addEventListener('popstate', function () {
    if (q('.cc-melon-shop')) go(window.location.href, false);
  });

  // sticky header section: only the pills row stays visible (the rest slides up under the site header).
  // The offset is the real bottom of the site's sticky header - the theme's --header-height variable goes
  // stale (it can read 168px while the header is 119px), which left a strip of the Melon header showing.
  function stickyHeader() {
    var els = document.querySelectorAll('.shopify-section-group-header-group, .cc-header--sticky');
    for (var i = 0; i < els.length; i++) {
      var pos = window.getComputedStyle(els[i]).position;
      if (pos === 'sticky' || pos === 'fixed') return els[i];
    }
    return null;
  }
  function measure() {
    var shop = q('.cc-melon-shop');
    var pills = q('.melon-shop__pills');
    if (!shop) return;
    var hide = pills ? shop.offsetHeight - pills.offsetHeight : shop.offsetHeight;
    var header = stickyHeader();
    var top = header ? Math.max(0, header.offsetHeight + (parseFloat(window.getComputedStyle(header).top) || 0)) : 0;
    shop.style.setProperty('--melon-collapse', hide + 'px');
    shop.style.setProperty('--melon-top', top + 'px');
  }
  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(measure);
    var watch = function () {
      var s = q('.cc-melon-shop'); if (s) ro.observe(s);
      var h = stickyHeader(); if (h) ro.observe(h);
      measure();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
  }
  window.addEventListener('load', measure);
})();
